import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Mail,
  Phone,
  Calendar,
  FileText,
  Briefcase,
  RefreshCw,
  Filter,
  MoreHorizontal,
  Clock,
  CheckCircle2,
  User,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ActivityItem } from '@/hooks/useAnalytics';

interface ActivityTimelineProps {
  activities: ActivityItem[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

type ActivityFilter = 'all' | 'email' | 'call' | 'meeting' | 'deal' | 'note';

const activityConfig: Record<ActivityItem['type'], { icon: React.ReactNode; label: string; color: string; bgColor: string }> = {
  email: {
    icon: <Mail className="h-4 w-4" />,
    label: 'Email',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-500/10',
  },
  call: {
    icon: <Phone className="h-4 w-4" />,
    label: 'Call',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-500/10',
  },
  meeting: {
    icon: <Calendar className="h-4 w-4" />,
    label: 'Meeting',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-500/10',
  },
  note: {
    icon: <FileText className="h-4 w-4" />,
    label: 'Note',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10',
  },
  deal: {
    icon: <Briefcase className="h-4 w-4" />,
    label: 'Deal',
    color: 'text-[#2B7FFF]',
    bgColor: 'bg-[#2B7FFF]/10',
  },
  status_change: {
    icon: <RefreshCw className="h-4 w-4" />,
    label: 'Status',
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-500/10',
  },
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatAbsoluteTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ActivityTimeline({
  activities,
  isLoading = false,
  onRefresh,
}: ActivityTimelineProps) {
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(activities.length);

  const filteredActivities = activities.filter(
    (activity) => filter === 'all' || activity.type === filter
  );

  // Auto-scroll to top when new activities arrive
  useEffect(() => {
    if (isAutoScroll && activities.length > prevLengthRef.current && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevLengthRef.current = activities.length;
  }, [activities.length, isAutoScroll]);

  const filters: { value: ActivityFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'email', label: 'Emails' },
    { value: 'call', label: 'Calls' },
    { value: 'meeting', label: 'Meetings' },
    { value: 'deal', label: 'Deals' },
    { value: 'note', label: 'Notes' },
  ];

  return (
    <Card className="overflow-hidden h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-[#2B7FFF]" />
            Activity Timeline
            {activities.length > 0 && (
              <Badge variant="secondary" className="text-xs bg-[#2B7FFF]/10 text-[#2B7FFF]">
                Live
              </Badge>
            )}
          </CardTitle>
          <CardDescription>Real-time activity feed</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsAutoScroll(!isAutoScroll)}
            className={cn(
              'h-8 w-8 transition-colors',
              isAutoScroll && 'text-[#2B7FFF]'
            )}
            title={isAutoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'}
          >
            <CheckCircle2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onRefresh} className="h-8 w-8">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Filter Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-hide">
          <Filter className="h-4 w-4 text-muted-foreground mr-1 flex-shrink-0" />
          {filters.map((f) => (
            <Button
              key={f.value}
              variant={filter === f.value ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setFilter(f.value)}
              className={cn(
                'h-7 text-xs whitespace-nowrap',
                filter === f.value && 'bg-[#2B7FFF] hover:bg-[#1A5FCC]'
              )}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Activity List */}
        <ScrollArea className="h-[400px] pr-4" ref={scrollRef}>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-5 top-0 bottom-0 w-px bg-gradient-to-b from-border via-border to-transparent" />

            <AnimatePresence mode="popLayout">
              {isLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7FFF]" />
                </div>
              ) : filteredActivities.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <div className="h-12 w-12 rounded-full bg-muted mx-auto mb-3 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground text-sm">No activities found</p>
                </motion.div>
              ) : (
                filteredActivities.map((activity, index) => {
                  const config = activityConfig[activity.type];
                  const isNew = index === 0 && new Date(activity.timestamp).getTime() > Date.now() - 60000;

                  return (
                    <motion.div
                      key={activity.id}
                      layout
                      initial={{ opacity: 0, x: -20, scale: 0.95 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: 20, scale: 0.95 }}
                      transition={{ 
                        duration: 0.3, 
                        delay: index < 5 ? index * 0.05 : 0,
                        layout: { duration: 0.2 }
                      }}
                      className={cn(
                        'relative flex gap-4 pb-5 group',
                        isNew && 'animate-pulse'
                      )}
                    >
                      {/* Timeline Dot */}
                      <div className="relative z-10 flex-shrink-0">
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: index * 0.05 + 0.1, type: 'spring', stiffness: 500 }}
                          className={cn(
                            'h-10 w-10 rounded-full border-2 border-background flex items-center justify-center transition-transform group-hover:scale-110',
                            config.bgColor
                          )}
                        >
                          <div className={config.color}>{config.icon}</div>
                        </motion.div>
                        {isNew && (
                          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 animate-ping" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-foreground text-sm">
                                {activity.title}
                              </span>
                              <Badge 
                                variant="secondary" 
                                className={cn('text-[10px] h-5', config.bgColor, config.color)}
                              >
                                {config.label}
                              </Badge>
                              {isNew && (
                                <Badge className="text-[10px] h-5 bg-red-500/10 text-red-600 dark:text-red-400">
                                  New
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-sm text-muted-foreground mt-1">
                              {activity.description}
                            </p>

                            <div className="flex items-center gap-3 mt-2">
                              {activity.contactName && (
                                <div className="flex items-center gap-1.5">
                                  <Avatar className="h-5 w-5">
                                    <AvatarFallback className="bg-[#2B7FFF]/10 text-[#2B7FFF] text-[10px]">
                                      {activity.contactName.split(' ').map((n) => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-xs text-[#2B7FFF]">{activity.contactName}</span>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <User className="h-3 w-3" />
                                <span className="text-xs">{activity.performedBy}</span>
                              </div>
                            </div>
                          </div>

                          {/* Time */}
                          <div className="text-right flex-shrink-0">
                            <span 
                              className="text-xs text-muted-foreground"
                              title={formatAbsoluteTime(activity.timestamp)}
                            >
                              {formatRelativeTime(activity.timestamp)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>
          </div>
        </ScrollArea>

        {/* Footer Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>{filteredActivities.length} activities</span>
            <span>•</span>
            <span>Updated {formatRelativeTime(new Date().toISOString())}</span>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs">
            <MoreHorizontal className="h-3 w-3 mr-1" />
            View All
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default ActivityTimeline;
